// Columna central del explorer (`.compact-table` del mock): las columnas del fleet, ordenables, con
// selección de fila y el orden de vista de `sortFleet` como default. La lista es UNA: trae la flota
// entera y el proveedor es una columna más, no el eje de la pantalla.
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import {
    deviceHardwareLabel,
    getStatusPresentation,
    MDM_HEARTBEAT_COLUMN_HELP,
    MDM_LOCK_MODE_SHORT_LABEL,
    MDM_PROVIDER_LABEL,
    MISSING_LOCK_MODE_NOTE,
    NO_VALUE,
    readHeartbeat,
    readLockPhase,
    sortFleet,
    summarizeMissingHeartbeat,
} from '../logic';
import type { HeartbeatSeverity, MdmFleetDevice } from '../types';
import { MdmStatusPill } from './MdmStatusPill';

/** El expediente MDM no guarda al cliente: la celda lo dice en vez de quedar en blanco. */
const NO_CUSTOMER_TITLE = 'El expediente del equipo no guarda al cliente';

export type MdmFleetSortKey =
    | 'imei'
    | 'customer'
    | 'provider'
    | 'lockMode'
    | 'status'
    | 'heartbeat'
    | 'ops';
export type MdmFleetSortDirection = 'asc' | 'desc';

export interface MdmFleetSortState {
    key: MdmFleetSortKey;
    direction: MdmFleetSortDirection;
}

/** Lo operable y más callado arriba: el default de la pantalla es el orden de `sortFleet`. */
export const DEFAULT_FLEET_SORT: MdmFleetSortState = { key: 'heartbeat', direction: 'desc' };

/**
 * Las columnas de la lista. La del equipo NO es ordenable: ordenaría por `brand`/`model`, que el
 * expediente MDM no guarda, mientras la celda muestra el SKU — el operador creería que ordenó.
 */
interface FleetColumn {
    key: MdmFleetSortKey | 'device';
    label: string;
    width: string;
    numeric?: boolean;
    sortable: boolean;
    /** El nombre completo de la columna, para el tooltip del encabezado. */
    title?: string;
}

/**
 * Anchos en porcentaje: la tabla se angosta con la ventana y una suma fija la desborda. El crédito
 * no se lleva el reparto grande porque el expediente MDM no trae cliente. Suman 100.
 */
const COLUMNS: FleetColumn[] = [
    { key: 'imei', label: 'IMEI', width: 'w-[14%]', sortable: true },
    { key: 'customer', label: 'Crédito', width: 'w-[16%]', sortable: true },
    {
        key: 'device',
        label: 'Marca / Modelo',
        width: 'w-[11%]',
        sortable: false,
        title: 'Marca y modelo si el proveedor los reportó; si no, el SKU enrolado',
    },
    {
        key: 'provider',
        label: 'MDM',
        width: 'w-[11%]',
        sortable: true,
        title: 'Proveedor MDM en el que está enrolado el equipo',
    },
    {
        key: 'lockMode',
        label: 'Modo',
        width: 'w-[13%]',
        sortable: true,
        title: 'Cómo se dio de alta el equipo con el proveedor; en Trustonic se congela al enrolar',
    },
    { key: 'status', label: 'Estado', width: 'w-[14%]', sortable: true },
    {
        key: 'heartbeat',
        label: 'Señal',
        width: 'w-[14%]',
        sortable: true,
        title: MDM_HEARTBEAT_COLUMN_HELP,
    },
    { key: 'ops', label: 'Ops', width: 'w-[7%]', numeric: true, sortable: true },
];

/** La etiqueta corta del modo, o nada cuando el expediente no lo registra. */
function lockModeLabel(device: MdmFleetDevice): string | undefined {
    return device.mdmLockMode ? MDM_LOCK_MODE_SHORT_LABEL[device.mdmLockMode] : undefined;
}

/** El equipo que estuvo enrolado y el que nunca lo estuvo comparten el guion, no el motivo. */
function heartbeatTitle(device: MdmFleetDevice): string {
    if (device.lastSeenAt) return formatDateTimeMx(device.lastSeenAt);
    return summarizeMissingHeartbeat(device.mdmStatus);
}

/** Los numéricos y el heartbeat arrancan de mayor a menor: lo interesante es el extremo alto. */
const DEFAULT_DIRECTION: Record<MdmFleetSortKey, MdmFleetSortDirection> = {
    imei: 'asc',
    customer: 'asc',
    provider: 'asc',
    lockMode: 'asc',
    status: 'asc',
    heartbeat: 'desc',
    ops: 'desc',
};

const HEARTBEAT_CLASS: Record<HeartbeatSeverity, string> = {
    ok: 'text-foreground',
    warn: 'font-bold text-warning',
    danger: 'font-bold text-destructive',
    neutral: 'text-muted-foreground',
};

const CELL = 'border-b border-border/50 px-[3px] py-[5px] text-[11px] align-middle';

/** Click en un encabezado: alterna si ya ordenaba por esa columna, si no estrena su default. */
export function nextFleetSort(current: MdmFleetSortState, key: MdmFleetSortKey): MdmFleetSortState {
    if (current.key !== key) return { key, direction: DEFAULT_DIRECTION[key] };
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

/** Lo ausente va al final del orden ascendente: una celda vacía no compite por el primer lugar. */
function compareText(a: string | undefined, b: string | undefined): number {
    if (a === b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a.localeCompare(b, 'es-MX');
}

/** Ordena la flota YA filtrada. Vive fuera del componente: la página ordena antes de paginar. */
export function sortDevices(
    devices: readonly MdmFleetDevice[],
    sort: MdmFleetSortState,
    now: Date | string,
): MdmFleetDevice[] {
    if (sort.key === 'heartbeat') {
        // El orden de vista completo: operables por antigüedad, después los mudos y los terminales.
        const viewOrder = sortFleet(devices, now);
        return sort.direction === 'desc' ? viewOrder : viewOrder.reverse();
    }
    // `key` en un const: dentro del callback TS no conserva el estrechamiento de `sort.key`.
    const key = sort.key;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...devices].sort((a, b) => {
        switch (key) {
            case 'imei':
                return factor * compareText(a.imei, b.imei);
            case 'customer':
                return (
                    factor *
                    (compareText(a.creditRef, b.creditRef) ||
                        compareText(a.customerName, b.customerName))
                );
            case 'provider':
                return (
                    factor *
                    compareText(
                        MDM_PROVIDER_LABEL[a.mdmProvider],
                        MDM_PROVIDER_LABEL[b.mdmProvider],
                    )
                );
            case 'lockMode':
                return factor * compareText(lockModeLabel(a), lockModeLabel(b));
            case 'status':
                return (
                    factor *
                    // Por la etiqueta corta: es la que el operador ve en la columna.
                    compareText(
                        getStatusPresentation(a.mdmStatus).shortLabel,
                        getStatusPresentation(b.mdmStatus).shortLabel,
                    )
                );
            case 'ops':
                return factor * (a.pendingOperations - b.pendingOperations);
        }
    });
}

export interface MdmFleetTableProps {
    /** Equipos de ESTA página, ya filtrados y ordenados por la pantalla. */
    devices: readonly MdmFleetDevice[];
    /** El orden vigente. Vive en la pantalla porque ordenar tiene que pasar ANTES de paginar. */
    sort: MdmFleetSortState;
    onSortChange: (next: MdmFleetSortState) => void;
    /** Instante contra el que se mide la antigüedad del heartbeat. */
    now: Date | string;
    /** El crédito abierto en la ficha. El IMEI no sirve de llave: dos créditos pueden compartirlo. */
    selectedCreditId: string | null;
    onSelect: (creditId: string) => void;
    /** Por qué no hay filas: lo resuelve el padre, que sí sabe si la flota está vacía o filtrada. */
    emptyMessage: string;
    className?: string;
}

export function MdmFleetTable({
    devices,
    sort,
    onSortChange,
    now,
    selectedCreditId,
    onSelect,
    emptyMessage,
    className,
}: MdmFleetTableProps) {
    const rows = devices;

    return (
        <div className={cn('min-h-0 flex-1 overflow-auto', className)}>
            <table className="w-full min-w-[460px] table-fixed border-collapse">
                <caption className="sr-only">
                    Equipos del proveedor MDM seleccionado, con su estado, heartbeat y operaciones
                    pendientes.
                </caption>
                <thead className="sticky top-0 z-10 bg-muted">
                    <tr className="border-b border-border">
                        {COLUMNS.map((column) => {
                            const active = column.sortable && sort.key === column.key;
                            const headClass = cn(
                                'overflow-hidden px-[3px] py-[5px] align-bottom text-[9.5px] font-bold uppercase leading-tight tracking-[0.04em] text-muted-foreground',
                                column.width,
                                column.numeric ? 'text-right' : 'text-left',
                            );
                            // Sin `aria-sort` ni botón: la columna no promete un orden que no aplica.
                            if (!column.sortable) {
                                return (
                                    <th
                                        key={column.key}
                                        scope="col"
                                        title={column.title ?? column.label}
                                        className={headClass}
                                    >
                                        {column.label}
                                    </th>
                                );
                            }
                            const sortKey = column.key as MdmFleetSortKey;
                            return (
                                <th
                                    key={column.key}
                                    scope="col"
                                    aria-sort={
                                        active
                                            ? sort.direction === 'asc'
                                                ? 'ascending'
                                                : 'descending'
                                            : 'none'
                                    }
                                    title={column.title ?? column.label}
                                    className={headClass}
                                >
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onSortChange(nextFleetSort(sort, sortKey));
                                        }}
                                        className={cn(
                                            // El encabezado se parte —y el indicador baja de renglón— antes que invadir la columna de al lado.
                                            'inline-flex flex-wrap select-none items-center gap-0.5 whitespace-normal text-left uppercase transition-colors hover:text-foreground',
                                            active && 'text-foreground',
                                        )}
                                    >
                                        {column.label}
                                        {!active ? null : sort.direction === 'asc' ? (
                                            <ChevronUp className="size-2.5 shrink-0" aria-hidden />
                                        ) : (
                                            <ChevronDown
                                                className="size-2.5 shrink-0"
                                                aria-hidden
                                            />
                                        )}
                                    </button>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td
                                colSpan={COLUMNS.length}
                                className="px-2 py-6 text-center text-[12px] text-muted-foreground"
                            >
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : (
                        rows.map((device) => {
                            const heartbeat = readHeartbeat(device.lastSeenAt, now);
                            const selected = device.creditId === selectedCreditId;
                            const lockPhase = readLockPhase(device, now);
                            return (
                                <tr
                                    key={device.creditId}
                                    aria-selected={selected}
                                    onClick={() => {
                                        onSelect(device.creditId);
                                    }}
                                    className={cn(
                                        'cursor-pointer transition-colors',
                                        selected
                                            ? 'bg-success-soft/55 shadow-[inset_3px_0_0_hsl(var(--success))] hover:bg-success-soft/70'
                                            : 'hover:bg-accent/40',
                                    )}
                                >
                                    {/* Solo los últimos 8 dígitos: el IMEI completo vive en la ficha. */}
                                    <td className={cn(CELL, 'truncate font-mono text-[10.5px]')}>
                                        {`…${device.imei.slice(-8)}`}
                                    </td>
                                    <td className={cn(CELL, 'truncate')}>
                                        <div
                                            title={device.creditRef}
                                            className="truncate font-mono text-[10.5px]"
                                        >
                                            {device.creditRef}
                                        </div>
                                        <div
                                            title={device.customerName ?? NO_CUSTOMER_TITLE}
                                            className="truncate text-[9.5px] text-muted-foreground"
                                        >
                                            {device.customerName ?? NO_VALUE}
                                        </div>
                                    </td>
                                    <td className={cn(CELL, 'truncate')}>
                                        <div className="truncate">
                                            {device.brand ?? deviceHardwareLabel(device)}
                                        </div>
                                        {device.model === undefined ? null : (
                                            <div className="truncate text-[9.5px] text-muted-foreground">
                                                {device.model}
                                            </div>
                                        )}
                                    </td>
                                    <td
                                        className={cn(
                                            CELL,
                                            'truncate text-[10.5px] text-muted-foreground',
                                        )}
                                    >
                                        {MDM_PROVIDER_LABEL[device.mdmProvider]}
                                    </td>
                                    {/* Baja de renglón en vez de recortarse; el clip se conserva porque salió el `truncate`. */}
                                    <td
                                        title={lockModeLabel(device) ?? MISSING_LOCK_MODE_NOTE}
                                        className={cn(
                                            CELL,
                                            'overflow-hidden whitespace-normal text-[10.5px] leading-tight text-muted-foreground',
                                        )}
                                    >
                                        {lockModeLabel(device) ?? NO_VALUE}
                                    </td>
                                    <td className={CELL}>
                                        {/* Relleno recortado y dos líneas antes que desbordar: a 1280 la columna mide 65 px. */}
                                        <MdmStatusPill
                                            status={device.mdmStatus}
                                            lockPhase={lockPhase}
                                            mdmConfirmed={device.mdmConfirmed}
                                            lockScheduledFor={device.currentLockScheduledFor}
                                            compact
                                            className="max-w-full whitespace-normal px-1 text-left leading-tight"
                                        />
                                    </td>
                                    <td
                                        title={heartbeatTitle(device)}
                                        className={cn(
                                            CELL,
                                            'truncate tabular-nums text-[10.5px]',
                                            HEARTBEAT_CLASS[heartbeat.severity],
                                        )}
                                    >
                                        {heartbeat.label}
                                    </td>
                                    <td
                                        className={cn(
                                            CELL,
                                            'text-right tabular-nums',
                                            device.pendingOperations > 0 &&
                                                'font-bold text-warning',
                                        )}
                                    >
                                        {device.pendingOperations}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
}
